import { api, deviceId } from '../api/client';
import type { Moneda, Pedido, Producto, SyncPushResponse, Tasa } from '../api/types';
import { db, type OutboxItem, type PedidoLocal } from './db';

export interface NuevaLineaPedido {
  productoId: string;
  cantidad: number;
}

export interface NuevoPedidoLocal {
  clienteId: string;
  clienteNombre: string;
  clienteRif: string;
  vendedorNombre: string;
  moneda: Moneda;
  observaciones?: string;
  items: NuevaLineaPedido[];
}

const ROUND = (value: number): number =>
  Math.round((value + Number.EPSILON) * 10000) / 10000;

function tasaPara(moneda: Moneda, rates: Tasa[]): number {
  if (moneda === 'USD') return 1;
  const target = moneda === 'VES' ? 'USD' : moneda;
  const rate = rates.find((item) => item.moneda === target)?.tasa;
  return rate && rate > 0 ? rate : 1;
}

/**
 * Crea el pedido de forma optimista y lo encola. La UI ve el registro al
 * instante; el envío real ocurre en flushOutbox.
 */
export async function enqueuePedido(
  input: NuevoPedidoLocal,
  productos: Producto[],
  rates: Tasa[],
): Promise<PedidoLocal> {
  const productosPorId = new Map(productos.map((item) => [item.id, item]));
  const lineas = input.items.map((line) => {
    const producto = productosPorId.get(line.productoId);
    if (!producto) throw new Error('Producto no disponible en el catálogo local');
    const base = ROUND(producto.precioUsd * line.cantidad);
    const total = ROUND(base * (1 + producto.impuestoPct / 100));
    return { producto, cantidad: line.cantidad, base, total };
  });

  const totalUsd = ROUND(lineas.reduce((sum, line) => sum + line.total, 0));
  const cajas = ROUND(lineas.reduce((sum, line) => sum + line.cantidad, 0));
  const tasa = tasaPara(input.moneda, rates);
  const now = new Date().toISOString();
  const localId = `local-${crypto.randomUUID()}`;
  const idempotencyKey = crypto.randomUUID();
  const dayStamp = now.slice(0, 10).replaceAll('-', '');

  const order: PedidoLocal = {
    id: localId,
    numero: `PED-LOCAL-${dayStamp}-${idempotencyKey.slice(0, 4).toUpperCase()}`,
    clienteId: input.clienteId,
    clienteNombre: input.clienteNombre,
    clienteRif: input.clienteRif,
    vendedorNombre: input.vendedorNombre,
    estado: 'en_cola',
    moneda: input.moneda,
    tasaAplicada: tasa,
    totalUsd,
    totalMoneda: ROUND(totalUsd * tasa),
    cajas,
    createdAt: now,
    observaciones: input.observaciones,
    items: lineas.map((line) => ({
      productoId: line.producto.id,
      sku: line.producto.sku,
      nombre: line.producto.nombre,
      cantidad: line.cantidad,
      precioUnitarioUsd: line.producto.precioUsd,
      impuestoPct: line.producto.impuestoPct,
    })),
    syncStatus: 'queued',
  };

  const mutation: OutboxItem = {
    mutationId: crypto.randomUUID(),
    orderId: localId,
    entity: 'pedido',
    idempotencyKey,
    payload: {
      clienteId: input.clienteId,
      moneda: input.moneda,
      ...(input.observaciones ? { observaciones: input.observaciones } : {}),
      idempotencyKey,
      origen: 'offline',
      creadoOfflineEn: now,
      items: input.items.map((line) => ({
        productoId: line.productoId,
        cantidad: line.cantidad,
      })),
    },
    createdAt: now,
    status: 'queued',
    attempts: 0,
  };

  await db.transaction('rw', [db.orders, db.outbox], async () => {
    await db.orders.put(order);
    await db.outbox.add(mutation);
  });

  return order;
}

export interface FlushResult {
  skipped: boolean;
  recibidos: number;
  aplicados: number;
  duplicados: number;
  errores: number;
}

const EMPTY_RESULT: FlushResult = {
  skipped: true,
  recibidos: 0,
  aplicados: 0,
  duplicados: 0,
  errores: 0,
};

let flushing = false;

export async function flushOutbox(demo: boolean): Promise<FlushResult> {
  if (demo) return EMPTY_RESULT;
  if (!navigator.onLine) throw new Error('Sin conexión: la cola se enviará al reconectar');
  if (flushing) return EMPTY_RESULT;

  const pending = await db.outbox
    .where('status')
    .anyOf('queued', 'error')
    .limit(50)
    .toArray();
  if (pending.length === 0) return EMPTY_RESULT;

  flushing = true;
  try {
    await db.outbox.bulkUpdate(
      pending.map((item) => ({ key: item.seq!, changes: { status: 'syncing' as const } })),
    );

    let response: { data: SyncPushResponse };
    try {
      response = await api.push({
        deviceId: deviceId(),
        mutations: pending.map((item) => ({
          id: item.mutationId,
          entity: item.entity,
          idempotencyKey: item.idempotencyKey,
          createdAt: item.createdAt,
          payload: item.payload,
        })),
      });
    } catch (error) {
      await db.outbox.bulkUpdate(
        pending.map((item) => ({
          key: item.seq!,
          changes: {
            status: 'queued' as const,
            attempts: item.attempts + 1,
            error: error instanceof Error ? error.message : 'Error de red',
          },
        })),
      );
      throw error;
    }

    const results = new Map(response.data.results.map((item) => [item.id, item]));

    for (const item of pending) {
      const result = results.get(item.mutationId);
      if (!result) {
        await db.outbox.update(item.seq!, {
          status: 'error',
          error: 'El servidor no devolvió resultado para esta mutación',
          attempts: item.attempts + 1,
        });
        await db.orders.update(item.orderId, {
          syncStatus: 'error',
          syncError: 'Sin respuesta del servidor',
        });
        continue;
      }

      if (result.status === 'applied' || result.status === 'duplicate') {
        await db.outbox.update(item.seq!, {
          status: 'synced',
          error: undefined,
          attempts: item.attempts + 1,
        });
        const changes: Partial<PedidoLocal> = {
          syncStatus: 'synced',
          syncError: undefined,
          estado: 'en_cola',
        };
        if (result.serverId) changes.serverId = result.serverId;
        if (result.numero) changes.numero = result.numero;
        await db.orders.update(item.orderId, changes);
      } else {
        await db.outbox.update(item.seq!, {
          status: 'error',
          error: result.message ?? result.code ?? 'Error de negocio',
          attempts: item.attempts + 1,
        });
        await db.orders.update(item.orderId, {
          syncStatus: 'error',
          syncError: result.message ?? result.code ?? 'Error de negocio',
        });
      }
    }

    return {
      skipped: false,
      recibidos: response.data.summary.recibidos,
      aplicados: response.data.summary.aplicados,
      duplicados: response.data.summary.duplicados,
      errores: response.data.summary.errores,
    };
  } finally {
    flushing = false;
  }
}

export async function pendingOutboxCount(): Promise<number> {
  return db.outbox.where('status').anyOf('queued', 'syncing', 'error').count();
}

export function localOrderToPedido(order: PedidoLocal): Pedido {
  return {
    id: order.serverId ?? order.id,
    numero: order.numero,
    estado: (order.syncStatus === 'error' ? 'en_cola' : order.estado) as Pedido['estado'],
    moneda: order.moneda,
    tasaAplicada: order.tasaAplicada,
    totalUsd: order.totalUsd,
    totalMoneda: order.totalMoneda,
    createdAt: order.createdAt,
    origen: 'offline',
    clienteNombre: order.clienteNombre,
    clienteRif: order.clienteRif,
    vendedorNombre: order.vendedorNombre,
    cajas: order.cajas,
    local: order.syncStatus !== 'synced',
    syncStatus: order.syncStatus,
  };
}
